import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { HeaderBar } from '../components/ui';
import { API_BASE_URL } from '../services/api';
import { useTranslation } from '../i18n/useTranslation';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import './EventCodeEntry.css';

// Color palette - SPOOKY Theme
const colors = {
  primary: '#6366F1',
  primaryHover: '#4F46E5',
  accent: '#6366F1',
  accentSoft: 'rgba(99, 102, 241, 0.1)',
  background: '#F6F7FB',
  surface: '#FFFFFF',
  text: '#0B0F1A',
  muted: '#5B6273',
  border: 'rgba(15, 23, 42, 0.10)',
  success: '#22C55E',
  warning: '#F59E0B',
  error: '#EF4444',
};

function EventCodeEntry() {
  const navigate = useNavigate();
  const { showToast, authData } = useApp();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const firstName = authData?.name?.trim().split(/\s+/)[0] || authData?.name || '';
  const dateLocale = language === 'ar' ? 'ar-EG' : 'en-US';
  const [eventCode, setEventCode] = useState('');
  const [allEvents, setAllEvents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [copied, setCopied] = useState(false);
  const eventCodeInputRef = useRef(null);

  // Fetch all public events
  useEffect(() => {
    fetchAllEvents();
  }, []);

  const fetchAllEvents = async () => {
    try {
      const url = `${API_BASE_URL}/events/demonstrations`;
      const response = await fetch(url);
      const data = await response.json();
      setAllEvents(data.events || []);
    } catch (err) {
      console.error('Failed to fetch events:', err);
      setAllEvents([]);
    }
  };

  const handleJoinEvent = () => {
    if (eventCode.trim()) {
      navigate(`/event/${eventCode.trim()}`);
    }
  };

  const copyToClipboard = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      showToast(t('homepage.eventCodeCopied'), 'success');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  // Filter: upcoming events only, then by search
  const todayStart = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const filteredEvents = allEvents.filter(event => {
    const isUpcoming = event.event_date && new Date(event.event_date) >= todayStart;
    if (!isUpcoming) return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      event.event_name?.toLowerCase().includes(q) ||
      event.event_location?.toLowerCase().includes(q) ||
      event.event_code?.toLowerCase().includes(q)
    );
  });

  const valueProps = useMemo(() => [
    { icon: '🎯', title: t('homepage.featureEventCentric'), desc: t('homepage.featureEventCentricDesc'), color: colors.primary },
    { icon: '🤝', title: t('homepage.featureTrustFirst'), desc: t('homepage.featureTrustFirstDesc'), color: colors.success },
    { icon: '💬', title: t('homepage.featureConnectInstantly'), desc: t('homepage.featureConnectInstantlyDesc'), color: colors.warning },
    { icon: '🗺️', title: t('homepage.featureSmartMatching'), desc: t('homepage.featureSmartMatchingDesc'), color: '#8B5CF6' },
    { icon: '🔒', title: t('homepage.featurePrivacyControls'), desc: t('homepage.featurePrivacyControlsDesc'), color: '#EC4899' },
    { icon: '📱', title: t('homepage.featureNoAppNeeded'), desc: t('homepage.featureNoAppNeededDesc'), color: colors.primary },
  ], [t]);

  const impacts = useMemo(() => [
    { icon: '💰', value: '50%', label: t('homepage.savingsLabel') },
    { icon: '🚗', value: '40%', label: t('homepage.fewerCarsLabel') },
    { icon: '🌱', value: '2.5kg', label: t('homepage.co2SavedLabel') },
    { icon: '⏱️', value: '∞', label: t('homepage.connectionsLabel') },
  ], [t]);

  const isRTL = language === 'ar';
  const pageStyle = {
    ...styles.page,
    fontFamily: isRTL ? "'Cairo', 'Segoe UI', Tahoma, sans-serif" : "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
    direction: isRTL ? 'rtl' : 'ltr',
  };

  return (
    <div style={pageStyle} dir={isRTL ? 'rtl' : 'ltr'}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700&display=swap" rel="stylesheet" />
      
      <style>{`
        * { box-sizing: border-box; }
        html { scroll-behavior: smooth; -webkit-tap-highlight-color: transparent; }
        input { font-family: inherit; }
        
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-12px); }
        }
        
        .animate-in { animation: fadeInUp 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards; opacity: 0; }
        .delay-1 { animation-delay: 0.1s; }
        .delay-2 { animation-delay: 0.2s; }
        .delay-3 { animation-delay: 0.3s; }
        .animate-float { animation: float 4s ease-in-out infinite; }
        
        .touch-active { transition: transform 0.15s ease, box-shadow 0.15s ease; }
        .touch-active:active { transform: scale(0.97); }
        
        .hover-lift { transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease; }
        .hover-lift:hover { transform: translateY(-4px); box-shadow: 0 12px 40px rgba(99,102,241,0.12); border-color: ${colors.primary}; }
        
        .gradient-text {
          background: linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryHover} 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        
        /* RTL: align text to the right for Arabic */
        [dir="rtl"] .hero-content {
          text-align: right !important;
        }
        [dir="rtl"] .hero-subtitle {
          margin-left: 0;
          margin-right: auto;
        }
        [dir="rtl"] .hero-input-wrapper {
          margin-left: 0;
          margin-right: auto;
        }
        [dir="rtl"] .hero-stats {
          justify-content: flex-start !important;
        }
        [dir="rtl"] .section-header-home {
          text-align: center !important;
        }
        [dir="rtl"] .section-inner-home {
          direction: rtl;
        }
        [dir="rtl"] .event-card-home {
          text-align: right;
        }
        [dir="rtl"] .cta-content-home {
          text-align: right !important;
        }
        /* RTL: center Latest Events, Features, Impact sections */
        [dir="rtl"] .events-section-inner {
          text-align: center !important;
        }
        [dir="rtl"] .events-section-inner .section-header-home {
          text-align: center !important;
        }
        [dir="rtl"] .search-container-home {
          margin-left: auto !important;
          margin-right: auto !important;
        }
        [dir="rtl"] .features-section-inner .section-header-home,
        [dir="rtl"] .impact-section-inner .section-header-home {
          text-align: center !important;
        }
        
        @media (min-width: 900px) {
          .hero-container {
            display: grid !important;
            grid-template-columns: 1fr 1fr !important;
            gap: 80px !important;
            align-items: center !important;
          }
          .hero-content {
            text-align: left !important;
          }
          [dir="rtl"] .hero-container {
            direction: rtl;
          }
          [dir="rtl"] .hero-content {
            text-align: right !important;
          }
          .hero-subtitle {
            margin: 0 0 32px !important;
          }
          .hero-input-wrapper {
            margin: 0 0 32px !important;
          }
          [dir="rtl"] .events-section-inner,
          [dir="rtl"] .events-section-inner .section-header-home,
          [dir="rtl"] .features-section-inner .section-header-home,
          [dir="rtl"] .impact-section-inner .section-header-home {
            text-align: center !important;
          }
          [dir="rtl"] .search-container-home {
            margin-left: auto !important;
            margin-right: auto !important;
          }
          [dir="rtl"] .events-grid-home {
            direction: rtl;
            text-align: right;
          }
          [dir="rtl"] .events-grid-home .event-card-home {
            text-align: right;
          }
        }
      `}</style>

      {/* Header */}
      <HeaderBar />

      {/* Hero Section */}
      <section style={styles.hero}>
        <div style={styles.heroDecor1} className="animate-float" />
        <div style={styles.heroDecor2} />
        
        <div style={styles.heroContainer} className="hero-container">
          <div style={styles.heroContent} className="hero-content">
            <div style={styles.heroTag} className="animate-in">
              <span>✨</span>
              <span>{t('homepage.heroTag')}</span>
            </div>
            {firstName && (
              <p style={styles.heroWelcome} className="animate-in delay-1">
                {t('homepage.welcomeName')}{language === 'ar' ? '، ' : ', '}{firstName}
              </p>
            )}
            <h1 style={styles.heroTitle} className="animate-in delay-1">
              {t('homepage.heroTitleLine1')}<br />
              <span className="gradient-text">{t('homepage.heroTitleLine2')}</span><br />
              {t('homepage.heroTitleLine3')}
            </h1>
            
            <p style={styles.heroSubtitle} className="animate-in delay-2 hero-subtitle">
              {t('homepage.heroSubtitle')}
            </p>

            <div style={styles.heroInputWrapper} className="animate-in delay-3 hero-input-wrapper">
              <div style={styles.heroInputContainer} ref={eventCodeInputRef}>
                <span style={styles.heroInputIcon}>🔗</span>
                <input
                  type="text"
                  placeholder={t('homepage.eventCodePlaceholder')}
                  style={styles.heroInput}
                  value={eventCode}
                  onChange={(e) => setEventCode(e.target.value.toUpperCase())}
                  onFocus={e => e.target.parentElement.style.borderColor = colors.primary}
                  onBlur={e => e.target.parentElement.style.borderColor = colors.border}
                  onKeyDown={(e) => e.key === 'Enter' && handleJoinEvent()}
                />
                <button style={styles.heroInputBtn} onClick={handleJoinEvent} className="touch-active">{t('homepage.join')}</button>
              </div>
              <button
                style={styles.heroCreateEventUnder}
                onClick={() => navigate('/create-event')}
                className="touch-active"
              >
                <span>✨</span> {t('homepage.createEventBtn')}
              </button>
            </div>
          </div>

          <div style={styles.heroVisual} className="animate-in delay-2">
            <div style={styles.previewCard}>
              <div style={styles.previewHeader}>
                <span style={styles.previewDot} />
                <span style={styles.previewTitle}>🎉 {t('homepage.previewEventTitle')}</span>
                <span style={styles.previewBadge}>{t('homepage.previewRidesCount')}</span>
              </div>
              
              <div style={{ ...styles.rideCard, borderColor: colors.primary, background: `${colors.accentSoft}` }}>
                <div style={styles.rideDriver}>
                  <div style={styles.rideAvatar}>DK</div>
                  <div style={styles.rideInfo}>
                    <div style={styles.rideName}>David K. <span style={styles.verifiedBadge}>✓</span></div>
                    <div style={styles.rideRoute}>{t('homepage.rideRouteSample1')}</div>
                  </div>
                </div>
                <div style={styles.rideSeats}>
                  <span style={styles.seatFilled}>👤</span>
                  <span style={styles.seatFilled}>👤</span>
                  <span style={styles.seatEmpty}>+</span>
                  <span style={styles.seatEmpty}>+</span>
                </div>
              </div>
              
              <div style={styles.rideCard}>
                <div style={styles.rideDriver}>
                  <div style={{ ...styles.rideAvatar, background: `linear-gradient(135deg, ${colors.success}, #16A34A)` }}>RL</div>
                  <div style={styles.rideInfo}>
                    <div style={styles.rideName}>Rachel L. <span style={styles.verifiedBadge}>✓</span></div>
                    <div style={styles.rideRoute}>{t('homepage.rideRouteSample2')}</div>
                  </div>
                </div>
                <div style={styles.rideSeats}>
                  <span style={styles.seatFilled}>👤</span>
                  <span style={styles.seatEmpty}>+</span>
                  <span style={styles.seatEmpty}>+</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Latest Events Section */}
      {allEvents.length > 0 && (
        <section style={styles.eventsSection}>
          <div style={styles.sectionInner} className="events-section-inner">
            <div style={{ ...styles.sectionHeader, marginBottom: '20px' }} className="section-header-home">
              <span style={styles.sectionTag}>📅 {t('homepage.latestEventsTag')}</span>
              <h2 style={styles.sectionTitle}>{t('homepage.discoverEvents')}</h2>
              <p style={styles.sectionSubtitle}>{t('homepage.discoverSubtitle')}</p>
            </div>

            {/* Search Bar */}
            <div style={styles.searchContainer} className="search-container-home">
              <span style={styles.searchIcon}>🔍</span>
              <input
                type="text"
                placeholder={t('homepage.searchPlaceholder')}
                style={styles.searchInput}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Events Grid */}
            <div style={styles.eventsGrid} className="events-grid-home">
              {filteredEvents.slice(0, 6).map((event, index) => (
                <motion.div
                  key={event.event_id}
                  style={styles.eventCard}
                  className="hover-lift event-card-home"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => navigate(`/event/${event.event_code}`)}
                >
                  {event.event_image ? (
                    <div style={{ ...styles.eventImage, backgroundImage: `url(${event.event_image})` }} />
                  ) : (
                    <div style={styles.eventImagePlaceholder}>
                      <img src="/images/trempi-logo.png" alt="" style={styles.eventPlaceholderLogo} />
                    </div>
                  )}
                  
                  <div style={styles.eventContent}>
                    <h3 style={styles.eventName}>{event.event_name}</h3>
                    
                    {event.event_date && (
                      <p style={styles.eventDate}>
                        📅 {new Date(event.event_date).toLocaleDateString(dateLocale, {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </p>
                    )}
                    
                    {event.event_location && (
                      <p style={styles.eventLocation}>
                        📍 {event.event_location}
                      </p>
                    )}

                    <div style={styles.eventCardActions}>
                      <button
                        style={styles.eventCardCtaOffer}
                        onClick={(e) => { e.stopPropagation(); navigate(`/event/${event.event_code}/publish?mode=offer`); }}
                        className="touch-active"
                      >
                        🚗 {t('homepage.offerTremp') || 'Offer a tremp'}
                      </button>
                      <button
                        style={styles.eventCardCtaRequest}
                        onClick={(e) => { e.stopPropagation(); navigate(`/event/${event.event_code}/publish?mode=request`); }}
                        className="touch-active"
                      >
                        🙋 {t('homepage.requestTremp') || 'Request a tremp'}
                      </button>
                    </div>

                    <div style={styles.eventFooter}>
                      <code style={styles.eventCode}>{event.event_code}</code>
                      <button
                        style={styles.copyBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(event.event_code);
                        }}
                      >
                        {copied ? '✓' : '📋'}
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {filteredEvents.length === 0 && searchQuery.trim() && (
              <p style={styles.noResults}>{t('homepage.noEventsFound')} "{searchQuery}"</p>
            )}
          </div>
        </section>
      )}

      {/* Features Section */}
      <section style={{ ...styles.section, background: colors.surface }}>
        <div style={styles.sectionInner} className="features-section-inner">
          <div style={styles.sectionHeader} className="section-header-home">
            <span style={styles.sectionTag}>⚡ {t('homepage.featuresTag')}</span>
            <h2 style={styles.sectionTitle}>{t('homepage.everythingYouNeed')}</h2>
            <p style={styles.sectionSubtitle}>{t('homepage.featuresSubtitle')}</p>
          </div>

          <div style={styles.featuresGrid}>
            {valueProps.map((prop, idx) => (
              <div key={idx} style={styles.featureCard} className="hover-lift">
                <div style={{ ...styles.featureIcon, background: `${prop.color}15` }}>{prop.icon}</div>
                <h3 style={styles.featureTitle}>{prop.title}</h3>
                <p style={styles.featureDesc}>{prop.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact */}
      <section style={{ ...styles.section, background: colors.accentSoft }}>
        <div style={styles.sectionInner} className="impact-section-inner">
          <div style={styles.sectionHeader} className="section-header-home">
            <span style={{ ...styles.sectionTag, background: '#fff' }}>🌍 {t('homepage.impactTag')}</span>
            <h2 style={styles.sectionTitle}>{t('homepage.betterForEveryone')}</h2>
            <p style={styles.sectionSubtitle}>{t('homepage.impactSubtitle')}</p>
          </div>

          <div style={styles.impactGrid}>
            {impacts.map((item, idx) => (
              <div key={idx} style={styles.impactCard} className="hover-lift">
                <div style={styles.impactIcon}>{item.icon}</div>
                <div style={styles.impactValue}>{item.value}</div>
                <div style={styles.impactLabel}>{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ ...styles.section, background: colors.surface }}>
        <div style={styles.ctaContent}>
          <h2 style={styles.sectionTitle}>{t('homepage.ctaTitle')}</h2>
          <p style={styles.sectionSubtitle}>{t('homepage.ctaSubtitle')}</p>
          
          <div style={styles.ctaButtons}>
            <button style={styles.ctaPrimary} onClick={() => navigate('/create-event')} className="touch-active">
              <span>✨</span> {t('homepage.createEventBtn')}
            </button>
            <button style={styles.ctaSecondary} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="touch-active">
              <span>🔗</span> {t('homepage.joinEventBtn')}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: colors.background,
    color: colors.text,
    fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
    overflowX: 'hidden',
    paddingTop: '0',
  },

  // Hero
  hero: {
    minHeight: '90vh',
    display: 'flex',
    alignItems: 'center',
    padding: '60px 24px',
    position: 'relative',
    overflow: 'hidden',
  },
  heroDecor1: {
    position: 'absolute',
    top: '-100px',
    right: '-100px',
    width: '400px',
    height: '400px',
    borderRadius: '50%',
    background: colors.accentSoft,
    filter: 'blur(60px)',
    opacity: 0.8,
  },
  heroDecor2: {
    position: 'absolute',
    bottom: '100px',
    left: '-80px',
    width: '300px',
    height: '300px',
    borderRadius: '50%',
    background: `${colors.success}15`,
    filter: 'blur(60px)',
  },
  heroContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '48px',
    maxWidth: '1280px',
    margin: '0 auto',
    width: '100%',
  },
  heroContent: {
    position: 'relative',
    zIndex: 2,
    textAlign: 'center',
  },
  heroTag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    background: colors.accentSoft,
    padding: '8px 14px',
    borderRadius: '100px',
    fontSize: '12px',
    fontWeight: 600,
    color: colors.primary,
    marginBottom: '12px',
  },
  heroWelcome: {
    fontSize: 'clamp(15px, 2vw, 17px)',
    fontWeight: 600,
    color: colors.primary,
    marginBottom: '8px',
  },
  heroTitle: {
    fontSize: 'clamp(1.5rem, 4vw, 2.25rem)',
    fontWeight: 700,
    lineHeight: 1.15,
    marginBottom: '14px',
    letterSpacing: '-0.5px',
  },
  heroSubtitle: {
    fontSize: 'clamp(14px, 2vw, 16px)',
    color: colors.muted,
    lineHeight: 1.6,
    marginBottom: '24px',
    maxWidth: '540px',
    margin: '0 auto 24px',
  },
  heroInputWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    maxWidth: '520px',
    margin: '0 auto 32px',
  },
  heroCreateEventBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    width: '100%',
    maxWidth: '420px',
    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryHover} 100%)`,
    color: '#fff',
    border: 'none',
    padding: '14px 24px',
    borderRadius: '14px',
    fontSize: '16px',
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: `0 8px 24px ${colors.primary}40`,
    transition: 'all 0.2s ease',
  },
  heroInputContainer: {
    display: 'flex',
    alignItems: 'center',
    background: colors.surface,
    borderRadius: '14px',
    padding: '6px',
    border: `2px solid ${colors.border}`,
    boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
    transition: 'border-color 0.2s',
  },
  heroInputIcon: {
    padding: '0 14px',
    fontSize: '20px',
  },
  heroInput: {
    flex: 1,
    border: 'none',
    background: 'transparent',
    fontSize: '16px',
    padding: '14px 0',
    outline: 'none',
    color: colors.text,
    minWidth: 0,
  },
  heroInputBtn: {
    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryHover} 100%)`,
    color: '#fff',
    border: 'none',
    padding: '14px 24px',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: `0 4px 12px ${colors.primary}30`,
  },
  heroCreateEventUnder: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    width: '100%',
    background: 'transparent',
    color: colors.primary,
    border: `2px solid ${colors.primary}`,
    padding: '12px 20px',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  rideActions: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
  },
  offerBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    background: `linear-gradient(135deg, ${colors.success} 0%, #16A34A 100%)`,
    color: '#fff',
    border: 'none',
    padding: '14px 20px',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: `0 4px 12px ${colors.success}30`,
  },
  requestBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    background: colors.accentSoft,
    color: colors.primary,
    border: `2px solid ${colors.primary}`,
    padding: '12px 20px',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  heroVisual: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
  },
  previewCard: {
    background: colors.surface,
    borderRadius: '24px',
    padding: '24px',
    boxShadow: '0 16px 48px rgba(11,42,74,0.1)',
    border: `1px solid ${colors.border}`,
    width: '100%',
    maxWidth: '420px',
  },
  previewHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '20px',
    paddingBottom: '16px',
    borderBottom: `1px solid ${colors.border}`,
  },
  previewDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    background: colors.success,
    boxShadow: `0 0 8px ${colors.success}60`,
  },
  previewTitle: {
    flex: 1,
    fontSize: '15px',
    fontWeight: 600,
  },
  previewBadge: {
    background: colors.accentSoft,
    color: colors.primary,
    padding: '6px 12px',
    borderRadius: '100px',
    fontSize: '13px',
    fontWeight: 600,
  },
  rideCard: {
    padding: '16px',
    background: colors.background,
    borderRadius: '16px',
    marginBottom: '12px',
    border: `2px solid transparent`,
  },
  rideDriver: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    marginBottom: '12px',
  },
  rideAvatar: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryHover})`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: '15px',
    fontWeight: 700,
    flexShrink: 0,
  },
  rideInfo: {
    flex: 1,
    minWidth: 0,
  },
  rideName: {
    fontSize: '15px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  verifiedBadge: {
    color: colors.success,
    fontSize: '14px',
  },
  rideRoute: {
    fontSize: '13px',
    color: colors.muted,
  },
  rideSeats: {
    display: 'flex',
    gap: '6px',
  },
  seatFilled: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    background: colors.accentSoft,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
  },
  seatEmpty: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    background: colors.background,
    border: `2px dashed ${colors.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    color: colors.muted,
  },

  // Events Section
  eventsSection: {
    padding: '32px 24px 40px',
    background: colors.background,
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    background: colors.surface,
    border: `2px solid ${colors.border}`,
    borderRadius: '14px',
    padding: '12px 20px',
    marginBottom: '20px',
    maxWidth: '600px',
    margin: '0 auto 20px',
  },
  searchIcon: {
    fontSize: '20px',
    marginRight: '12px',
  },
  searchInput: {
    flex: 1,
    border: 'none',
    background: 'transparent',
    fontSize: '16px',
    outline: 'none',
    color: colors.text,
  },
  eventsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '24px',
  },
  eventCard: {
    background: colors.surface,
    borderRadius: '20px',
    overflow: 'hidden',
    border: `1px solid ${colors.border}`,
    cursor: 'pointer',
  },
  eventImage: {
    width: '100%',
    height: '200px',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
  eventImagePlaceholder: {
    width: '100%',
    height: '200px',
    background: `linear-gradient(135deg, ${colors.accentSoft} 0%, rgba(99, 102, 241, 0.05) 100%)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  eventPlaceholderLogo: {
    width: '60%',
    height: 'auto',
    opacity: 0.6,
  },
  eventContent: {
    padding: '20px',
  },
  eventName: {
    fontSize: '18px',
    fontWeight: 600,
    marginBottom: '10px',
    lineHeight: 1.3,
  },
  eventDate: {
    fontSize: '14px',
    color: colors.muted,
    marginBottom: '6px',
  },
  eventLocation: {
    fontSize: '14px',
    color: colors.muted,
    marginBottom: '12px',
  },
  eventCardActions: {
    display: 'flex',
    gap: '10px',
    marginBottom: '14px',
    flexWrap: 'wrap',
  },
  eventCardCtaOffer: {
    flex: 1,
    minWidth: '120px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '10px 14px',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryHover} 100%)`,
    color: '#fff',
    boxShadow: `0 2px 8px ${colors.primary}30`,
    transition: 'all 0.2s ease',
  },
  eventCardCtaRequest: {
    flex: 1,
    minWidth: '120px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '10px 14px',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    border: `2px solid ${colors.primary}`,
    background: 'transparent',
    color: colors.primary,
    transition: 'all 0.2s ease',
  },
  eventFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: '16px',
    borderTop: `1px solid ${colors.border}`,
  },
  eventCode: {
    fontFamily: 'monospace',
    fontSize: '13px',
    fontWeight: 600,
    color: colors.primary,
    background: colors.accentSoft,
    padding: '6px 12px',
    borderRadius: '8px',
  },
  copyBtn: {
    background: 'none',
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontSize: '16px',
    transition: 'all 0.2s',
  },
  noResults: {
    textAlign: 'center',
    color: colors.muted,
    fontSize: '16px',
    padding: '40px 20px',
  },

  // Section
  section: {
    padding: '80px 24px',
  },
  sectionInner: {
    maxWidth: '1280px',
    margin: '0 auto',
  },
  sectionHeader: {
    textAlign: 'center',
    marginBottom: '48px',
  },
  sectionTag: {
    display: 'inline-block',
    background: colors.accentSoft,
    color: colors.primary,
    padding: '10px 18px',
    borderRadius: '100px',
    fontSize: '14px',
    fontWeight: 600,
    marginBottom: '16px',
  },
  sectionTitle: {
    fontSize: 'clamp(1.75rem, 5vw, 2.5rem)',
    fontWeight: 700,
    marginBottom: '16px',
    letterSpacing: '-0.5px',
  },
  sectionSubtitle: {
    fontSize: '17px',
    color: colors.muted,
    maxWidth: '600px',
    margin: '0 auto',
    lineHeight: 1.7,
  },

  // Features
  featuresGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '24px',
  },
  featureCard: {
    background: colors.surface,
    borderRadius: '20px',
    padding: '32px',
    border: `1px solid ${colors.border}`,
    textAlign: 'center',
  },
  featureIcon: {
    width: '72px',
    height: '72px',
    borderRadius: '18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
    fontSize: '32px',
  },
  featureTitle: {
    fontSize: '18px',
    fontWeight: 600,
    marginBottom: '10px',
  },
  featureDesc: {
    fontSize: '15px',
    color: colors.muted,
    lineHeight: 1.6,
  },

  // Impact
  impactGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
  },
  impactCard: {
    background: colors.surface,
    borderRadius: '20px',
    padding: '32px 24px',
    textAlign: 'center',
    border: `1px solid ${colors.border}`,
  },
  impactIcon: {
    fontSize: '40px',
    marginBottom: '12px',
  },
  impactValue: {
    fontSize: '2.25rem',
    fontWeight: 700,
    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryHover} 100%)`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  impactLabel: {
    fontSize: '15px',
    color: colors.muted,
    marginTop: '4px',
    lineHeight: 1.4,
  },

  // CTA
  ctaContent: {
    maxWidth: '600px',
    margin: '0 auto',
    textAlign: 'center',
  },
  ctaButtons: {
    display: 'flex',
    gap: '16px',
    justifyContent: 'center',
    marginTop: '32px',
    flexWrap: 'wrap',
  },
  ctaPrimary: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryHover} 100%)`,
    color: '#fff',
    border: 'none',
    padding: '18px 36px',
    borderRadius: '14px',
    fontSize: '17px',
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: `0 8px 24px ${colors.primary}30`,
    transition: 'all 0.3s ease',
  },
  ctaSecondary: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    background: colors.background,
    color: colors.primary,
    border: `2px solid ${colors.border}`,
    padding: '16px 36px',
    borderRadius: '14px',
    fontSize: '17px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
};

export default EventCodeEntry;
